import { Link } from 'react-router-dom';
import { FiCalendar, FiSun, FiBook, FiClock, FiArrowRight } from 'react-icons/fi';

/**
 * Página informativa del Calendario Hebreo (para acceder desde el Home)
 */
export default function CalendarioInfo() {
  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-2xl shadow-xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <FiCalendar className="w-12 h-12" />
            <h1 className="text-4xl font-bold">
              Calendario Hebreo
            </h1>
          </div>
          <p className="text-xl text-green-50 mb-6">
            Sistema dual Gregoriano/Hebreo Caraíta con lecturas diarias, festividades y eventos bíblicos.
          </p>
          
          <Link
            to="/calendario"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors shadow-lg"
          >
            Ir al Calendario
            <FiArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Características */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <FiCalendar className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Sistema Dual
            </h3>
            <p className="text-gray-600">
              Visualiza el calendario Gregoriano y Hebreo Caraíta en paralelo con sincronización automática.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiSun className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Festividades Bíblicas
            </h3>
            <p className="text-gray-600">
              Consulta las fechas de Shabbat, Pesaj, Shavuot, Yom Teruah, Yom Kippur, Sukkot y más.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiBook className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Lecturas Diarias
            </h3>
            <p className="text-gray-600">
              Accede a las lecturas de Torá y Profetas programadas para cada día según el ciclo caraíta.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiClock className="w-8 h-8 text-green-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Eventos Personalizados
            </h3>
            <p className="text-gray-600">
              Crea y gestiona tus propios eventos y recordatorios en el calendario (usuarios registrados).
            </p>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Contenido Disponible
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="text-4xl font-bold text-green-600 mb-2">2,690+</div>
              <div className="text-gray-600">Lecturas Disponibles</div>
              <p className="text-sm text-gray-500 mt-2">Lecturas de Torá, Profetas y Escritos</p>
            </div>
            
            <div className="text-center p-4">
              <div className="text-4xl font-bold text-teal-600 mb-2">54</div>
              <div className="text-gray-600">Parashas Caraítas</div>
              <p className="text-sm text-gray-500 mt-2">Divisiones semanales de la Torá</p>
            </div>
            
            <div className="text-center p-4">
              <div className="text-4xl font-bold text-blue-600 mb-2">474</div>
              <div className="text-gray-600">Lecturas Diarias</div>
              <p className="text-sm text-gray-500 mt-2">Lecturas programadas del año</p>
            </div>
          </div>
        </div>

        {/* Funcionalidades */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Funcionalidades Disponibles
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Vista Mensual Interactiva</h4>
                <p className="text-gray-600 text-sm">Navega por meses y años con información detallada de cada día. Visualiza Torá, Profetas y eventos en cada fecha.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Festividades Destacadas</h4>
                <p className="text-gray-600 text-sm">Las festividades bíblicas están resaltadas con colores distintivos: Shabbat (azul), Yom Kippur (rojo), Pesaj (amarillo), etc.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Lecturas de Torá y Profetas</h4>
                <p className="text-gray-600 text-sm">Cada día muestra las lecturas programadas según el ciclo anual caraíta. Haz clic para leer el pasaje completo.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Sincronización Gregoriano-Hebreo</h4>
                <p className="text-gray-600 text-sm">El sistema calcula automáticamente la correspondencia entre las fechas gregorianas y hebreas caraítas.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Exportar e Importar (Usuarios Registrados)</h4>
                <p className="text-gray-600 text-sm">Exporta el calendario a formatos estándar o importa eventos desde otras fuentes.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Acceso Sin Registro</h4>
                <p className="text-gray-600 text-sm">Puedes consultar todo el calendario, festividades y lecturas sin necesidad de crear una cuenta.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-8 text-center">
          <Link
            to="/calendario"
            className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors shadow-lg"
          >
            <FiCalendar className="w-5 h-5" />
            Ver Calendario Completo
          </Link>
        </div>
      </div>
    </div>
  );
}






